<template>
  <div class="min-h-screen bg-white">
    <TrustBar />
    <div class="pt-18"></div>
    <!-- Loading State -->
    <div v-if="loading" class="min-h-screen flex items-center justify-center">
      <div class="text-center">
        <div
          class="inline-block w-12 h-12 border-4 border-fuchsia-600 border-t-transparent rounded-full animate-spin"
        ></div>
        <p class="mt-4 text-gray-600">Chargement...</p>
      </div>
    </div>

    <!-- Error State -->
    <div
      v-else-if="error"
      class="min-h-screen flex items-center justify-center"
    >
      <div class="text-center">
        <div class="text-7xl mb-6">⚠️</div>
        <h3 class="text-2xl font-bold text-gray-900 mb-3">{{ error }}</h3>
        <router-link
          to="/cards/shopping-gift-cards"
          class="inline-block px-6 py-3 bg-fuchsia-600 text-white rounded-xl hover:bg-fuchsia-700 transition"
        >
          Retour aux cartes cadeaux
        </router-link>
      </div>
    </div>

    <!-- Content -->
    <template v-else>
      <!-- Breadcrumb -->
      <div class="bg-gray-50 py-3 border-b border-gray-200">
        <div class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <nav class="flex text-sm">
            <router-link to="/" class="text-gray-500 hover:text-gray-900"
              >Accueil</router-link
            >
            <span class="mx-2 text-gray-400">/</span>
            <router-link
              to="/cards/shopping-gift-cards"
              class="text-gray-500 hover:text-gray-900"
              >Cartes cadeaux</router-link
            >
            <span class="mx-2 text-gray-400">/</span>
            <span class="text-gray-900 font-medium">{{
              product?.productName
            }}</span>
          </nav>
        </div>
      </div>

      <main class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div class="grid gap-8 lg:gap-12 lg:grid-cols-12 lg:mt-0">
          <!-- Left Column: Product Image -->
          <div class="lg:col-span-5 relative lg:mt-12 mt-8">
            <div
              class="flex justify-center lg:flex-col gap-4 lg:sticky lg:top-32"
            >
              <div class="relative">
                <!-- Gradient background effect -->
                <div
                  class="absolute right-1/4 top-1/2 -translate-y-1/2 h-[calc(100%+3.5rem)] w-[200vw] -z-10 rounded-full lg:h-[calc(100%+10.5rem)] lg:-top-8 lg:translate-y-0 bg-gradient-to-r from-gray-100 via-gray-100 to-transparent/5"
                ></div>
                <img
                  :alt="product?.productName"
                  class="rounded-xl relative h-36 md:h-48 lg:h-64 w-auto lg:w-[400px] shadow-[0_0_4px_rgba(0,0,0,0.25)]"
                  :src="productLogo"
                />
              </div>
              <!-- USP Badges (Desktop) -->
              <div class="hidden lg:block relative">
                <ul class="flex flex-wrap gap-2 items-center lg:items-start">
                  <li
                    class="inline-flex items-center gap-2 bg-gray-100 h-8 rounded-full px-4 w-auto"
                  >
                    <svg
                      class="w-4 h-4"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        stroke-linecap="round"
                        stroke-linejoin="round"
                        stroke-width="2"
                        d="M13 10V3L4 14h7v7l9-11h-7z"
                      />
                    </svg>
                    <span class="text-xs">Livraison en ligne instantanée</span>
                  </li>
                  <li
                    class="inline-flex items-center gap-2 bg-gray-100 h-8 rounded-full px-4 w-auto"
                  >
                    <svg
                      class="w-4 h-4"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        stroke-linecap="round"
                        stroke-linejoin="round"
                        stroke-width="2"
                        d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z"
                      />
                    </svg>
                    <span class="text-xs">Paiement sûr et sécurisé</span>
                  </li>
                </ul>
              </div>
            </div>
          </div>

          <!-- Right Column: Form -->
          <div
            class="lg:col-span-7 flex flex-1 flex-col items-center lg:items-start mt-3 lg:mt-2"
          >
            <div class="w-full flex flex-1 flex-col mb-5 lg:mb-6">
              <form @submit.prevent="goToCheckout" class="flex flex-col">
                <!-- Title -->
                <div class="flex flex-row flex-wrap gap-x-2 items-center">
                  <h1 class="font-bold text-xl md:text-3xl mb-2">
                    {{ product?.productName }} {{ country?.countryName || '' }}
                  </h1>
                </div>

                <!-- Country and Delivery Info -->
                <div class="flex flex-wrap gap-3 md:gap-4 mt-5 mb-6">
                  <!-- Country Selector -->
                  <div class="flex flex-1 min-w-[200px] md:flex-initial">
                    <button
                      type="button"
                      class="rounded-2xl flex items-center flex-1 justify-between border border-gray-200 bg-white px-4 py-3 hover:border-gray-300 hover:shadow-sm transition-all duration-200 group"
                    >
                      <div class="flex items-center gap-3">
                        <div
                          class="flex items-center justify-center bg-gradient-to-br from-gray-50 to-gray-100 rounded-xl w-11 h-11 shadow-inner"
                        >
                          <span v-if="country?.localLogoUrl">
                            <img
                              :src="country.localLogoUrl"
                              :alt="country.countryName"
                              class="w-6 h-4 object-cover rounded-sm"
                            />
                          </span>
                          <span
                            v-else
                            class="text-base font-bold text-gray-700"
                            >{{ country?.countryCode || 'BJ' }}</span
                          >
                        </div>
                        <div class="text-left">
                          <span class="text-xs text-gray-500 block"
                            >Pays d'utilisation</span
                          >
                          <span class="text-sm font-bold text-gray-900">{{
                            country?.countryName || 'Bénin'
                          }}</span>
                        </div>
                      </div>
                    </button>
                  </div>
                  <!-- Currency Display -->
                  <div class="flex flex-1 min-w-[200px] md:flex-initial">
                    <button
                      type="button"
                      class="rounded-2xl flex items-center flex-1 justify-between border border-gray-200 bg-white px-4 py-3 hover:border-gray-300 hover:shadow-sm transition-all duration-200 group"
                    >
                      <div class="flex items-center gap-3">
                        <div
                          class="flex items-center justify-center bg-gradient-to-br from-amber-50 to-orange-50 rounded-xl w-11 h-11 shadow-inner"
                        >
                          <svg viewBox="0 0 100 100" class="w-full h-full">
                            <text
                              x="50"
                              y="46"
                              font-size="4.25em"
                              dominant-baseline="central"
                              text-anchor="middle"
                              class="fill-amber-600"
                            >
                              {{ productCurrencySymbol }}
                            </text>
                          </svg>
                        </div>
                        <div class="text-left">
                          <span class="text-xs text-gray-500 block"
                            >Devise du produit</span
                          >
                          <span
                            class="text-sm font-bold text-gray-900 uppercase"
                            >{{ productCurrencySymbol }}</span
                          >
                        </div>
                      </div>
                    </button>
                  </div>
                  <!-- Delivery Type -->
                  <div class="flex flex-1 min-w-[200px] md:flex-initial">
                    <button
                      type="button"
                      class="rounded-2xl flex items-center flex-1 justify-between border border-gray-200 bg-white px-4 py-3 hover:border-gray-300 hover:shadow-sm transition-all duration-200 group"
                    >
                      <div class="flex items-center gap-3">
                        <div
                          class="flex items-center justify-center bg-gradient-to-br from-fuchsia-50 to-pink-50 rounded-xl w-11 h-11 shadow-inner"
                        >
                          <svg
                            class="w-6 h-6 text-fuchsia-600"
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                          >
                            <path
                              stroke-linecap="round"
                              stroke-linejoin="round"
                              stroke-width="2"
                              d="M12 18h.01M8 21h8a2 2 0 002-2V5a2 2 0 00-2-2H8a2 2 0 00-2 2v14a2 2 0 002 2z"
                            />
                          </svg>
                        </div>
                        <div class="text-left">
                          <span class="text-xs text-gray-500 block"
                            >Livraison</span
                          >
                          <span class="text-sm font-bold text-gray-900"
                            >À l'écran + email</span
                          >
                        </div>
                      </div>
                    </button>
                  </div>
                </div>

                <!-- Amount Selection -->
                <div class="mb-6">
                  <fieldset>
                    <legend class="sr-only">Sélectionnez un montant</legend>
                    <div class="grid grid-cols-2 sm:grid-cols-3 gap-3">
                      <button
                        v-for="amount in availableAmounts"
                        :key="amount"
                        type="button"
                        @click="
                          selectedAmount = amount;
                          customAmount = '';
                        "
                        class="relative overflow-hidden cursor-pointer rounded-2xl bg-white border-2 h-16 transition-all duration-200 group"
                        :class="
                          selectedAmount === amount && !customAmount
                            ? 'border-fuchsia-600 shadow-lg shadow-fuchsia-100 scale-[1.02]'
                            : 'border-gray-200 hover:border-fuchsia-300 hover:shadow-md'
                        "
                      >
                        <div
                          class="absolute inset-0 bg-gradient-to-br transition-opacity duration-200"
                          :class="
                            selectedAmount === amount && !customAmount
                              ? 'from-fuchsia-50 to-pink-50 opacity-100'
                              : 'from-gray-50 to-transparent opacity-0 group-hover:opacity-100'
                          "
                        ></div>
                        <div
                          class="relative h-full flex justify-center items-center"
                        >
                          <span
                            class="text-base font-bold"
                            :class="
                              selectedAmount === amount && !customAmount
                                ? 'text-fuchsia-700'
                                : 'text-gray-700'
                            "
                          >
                            {{ amount }} {{ productCurrencySymbol }}
                          </span>
                        </div>
                      </button>
                    </div>

                    <!-- Custom Amount Input (for range amounts) -->
                    <div v-if="isRangeAmount" class="mt-5">
                      <label
                        class="block text-gray-900 text-sm font-semibold mb-2"
                      >
                        Ou entrez un montant personnalisé ({{ minAmount }} -
                        {{ maxAmount }} {{ productCurrencySymbol }})
                      </label>
                      <div class="flex gap-3 items-center">
                        <div class="flex-1 relative">
                          <input
                            v-model="customAmount"
                            @input="validateCustomAmount"
                            type="number"
                            :min="minAmount"
                            :max="maxAmount"
                            :step="product.value?.senderDenominationStep || 1"
                            placeholder="Entrez un montant"
                            class="w-full border-2 border-gray-200 rounded-2xl h-14 px-4 pr-16 font-semibold text-gray-900 placeholder:font-normal placeholder-gray-400 transition-all duration-200 focus:border-fuchsia-500 focus:ring-4 focus:ring-fuchsia-100 outline-none"
                            @focus="
                              selectedAmount = parseFloat(customAmount) || null
                            "
                          />
                          <span
                            class="absolute right-4 top-1/2 -translate-y-1/2 text-gray-500 font-medium"
                            >{{ productCurrencySymbol }}</span
                          >
                        </div>
                      </div>
                      <p
                        v-if="customAmountError"
                        class="text-red-500 text-sm mt-2 flex items-center gap-1"
                      >
                        <svg
                          class="w-4 h-4"
                          fill="currentColor"
                          viewBox="0 0 20 20"
                        >
                          <path
                            fill-rule="evenodd"
                            d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z"
                            clip-rule="evenodd"
                          />
                        </svg>
                        {{ customAmountError }}
                      </p>
                    </div>
                  </fieldset>
                </div>

                <!-- Quantity Selector -->
                <div class="mb-6">
                  <div
                    class="inline-flex items-center gap-4 bg-white border-2 border-gray-200 rounded-2xl px-4 py-3 hover:border-gray-300 transition-colors duration-200"
                  >
                    <span class="text-sm font-semibold text-gray-700"
                      >Quantité</span
                    >
                    <div class="flex items-center gap-2">
                      <button
                        type="button"
                        @click="decrementQuantity"
                        :disabled="quantity <= 1"
                        class="w-9 h-9 flex items-center justify-center rounded-xl bg-gray-100 hover:bg-gray-200 active:bg-gray-300 transition-all disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:bg-gray-100"
                      >
                        <svg
                          class="w-4 h-4 text-gray-700"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path
                            stroke-linecap="round"
                            stroke-linejoin="round"
                            stroke-width="2.5"
                            d="M20 12H4"
                          />
                        </svg>
                      </button>
                      <span
                        class="font-bold text-lg min-w-[28px] text-center text-gray-900"
                        >{{ quantity }}</span
                      >
                      <button
                        type="button"
                        @click="incrementQuantity"
                        class="w-9 h-9 flex items-center justify-center rounded-xl bg-fuchsia-100 hover:bg-fuchsia-200 active:bg-fuchsia-300 transition-all"
                      >
                        <svg
                          class="w-4 h-4 text-fuchsia-700"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path
                            stroke-linecap="round"
                            stroke-linejoin="round"
                            stroke-width="2.5"
                            d="M12 4v16m8-8H4"
                          />
                        </svg>
                      </button>
                    </div>
                  </div>
                </div>

                <!-- Buy Button -->
                <div class="mb-6">
                  <button
                    type="submit"
                    class="w-full rounded-2xl flex justify-center items-center bg-gradient-to-r from-fuchsia-600 to-pink-600 hover:from-fuchsia-700 hover:to-pink-700 text-white min-h-14 h-14 font-bold text-base transition-all duration-200 shadow-lg shadow-fuchsia-200 hover:shadow-xl hover:shadow-fuchsia-300 active:scale-[0.98]"
                    :disabled="!selectedAmount"
                    :class="{
                      'opacity-50 cursor-not-allowed shadow-none hover:shadow-none active:scale-100':
                        !selectedAmount
                    }"
                  >
                    <span
                      >Acheter • {{ formatPrice(calculateRecipientTotal()) }} {{ productCurrencySymbol }}</span
                    >
                  </button>
                  <p
                    v-if="!selectedAmount"
                    class="text-gray-500 text-sm mt-2 text-center"
                  >
                    Sélectionnez un montant pour continuer
                  </p>
                </div>

                <!-- Payment Methods -->
                <div
                  class="flex flex-col gap-4 items-center justify-centerlg:items-start mb-6"
                >
                  <div class="flex items-center gap-2">
                    <ul class="flex gap-2 p-0 m-0 list-none">
                      <li
                        class="border border-gray-200 rounded-sm p-1 bg-white flex items-center justify-center w-9 h-7"
                      >
                        <svg class="h-4 w-auto" viewBox="0 0 36 16" fill="none">
                          <text
                            x="0"
                            y="12"
                            font-size="10"
                            font-weight="bold"
                            fill="#1A1F71"
                          >
                            VISA
                          </text>
                        </svg>
                      </li>
                      <li
                        class="border border-gray-200 rounded-sm p-1 bg-white flex items-center justify-center w-9 h-7"
                      >
                        <svg class="h-4 w-auto" viewBox="0 0 36 16" fill="none">
                          <circle cx="12" cy="8" r="6" fill="#EB001B" />
                          <circle cx="24" cy="8" r="6" fill="#F79E1B" />
                        </svg>
                      </li>
                      <li
                        class="border border-gray-200 rounded-sm p-1 bg-white flex items-center justify-center w-9 h-7"
                      >
                        <svg class="h-4 w-auto" viewBox="0 0 36 16" fill="none">
                          <text
                            x="0"
                            y="12"
                            font-size="8"
                            font-weight="bold"
                            fill="#003087"
                          >
                            PayPal
                          </text>
                        </svg>
                      </li>
                    </ul>
                  </div>
                </div>
              </form>

              <!-- USP Badges (Mobile) -->
              <div class="lg:hidden">
                <ul class="flex flex-wrap gap-2 items-center">
                  <li
                    class="inline-flex items-center gap-2 bg-gray-100 h-8 rounded-full px-4"
                  >
                    <svg
                      class="w-4 h-4"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        stroke-linecap="round"
                        stroke-linejoin="round"
                        stroke-width="2"
                        d="M13 10V3L4 14h7v7l9-11h-7z"
                      />
                    </svg>
                    <span class="text-xs">Livraison en ligne instantanée</span>
                  </li>
                  <li
                    class="inline-flex items-center gap-2 bg-gray-100 h-8 rounded-full px-4"
                  >
                    <svg
                      class="w-4 h-4"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        stroke-linecap="round"
                        stroke-linejoin="round"
                        stroke-width="2"
                        d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z"
                      />
                    </svg>
                    <span class="text-xs">Paiement sûr et sécurisé</span>
                  </li>
                </ul>
              </div>

            </div>
          </div>
        </div>

        <!-- About Section -->
        <aside class="lg:col-span-10 lg:col-start-2 mt-8 lg:mt-10">
          <div class="grid gap-8 lg:gap-12 lg:grid-cols-12">
            <div class="lg:col-span-5">
              <section>
                <h2 class="font-bold text-xl md:text-2xl mb-4">
                  À propos de {{ product?.productName }}
                </h2>
                <div class="text-gray-600 space-y-4">
                  <p>
                    Achetez un code de la carte cadeau
                    {{ product?.productName }} en ligne. Sélectionnez simplement
                    le montant dont vous avez besoin et payez par carte de
                    crédit ou PayPal.
                  </p>
                  <p>
                    Vous recevrez votre code par e-mail dans les 30 secondes.
                    C'est rapide, sûr et simple.
                  </p>
                </div>
              </section>
              <div class="mt-4 lg:mt-4 text-sm text-gray-500">
                <span>En utilisant ce service, vous acceptez les </span>
                <a href="#" class="underline hover:text-gray-900"
                  >Conditions générales</a
                >
                <span> de {{ product?.productName }}.</span>
              </div>
            </div>

            <!-- FAQ Section -->
            <div class="lg:col-span-7 flex flex-col">
              <section>
                <h2 class="font-bold text-xl md:text-2xl mb-5">
                  Questions fréquemment posées
                </h2>
                <div class="space-y-3">
                  <!-- FAQ Item 1 -->
                  <div
                    class="rounded-2xl bg-white border-2 border-gray-200 overflow-hidden transition-all duration-200"
                    :class="{ 'border-fuchsia-200 shadow-md': openFaq === 0 }"
                  >
                    <button
                      @click="toggleFaq(0)"
                      class="w-full text-left p-5 flex items-center justify-between hover:bg-gray-50 transition-colors duration-200"
                    >
                      <h3 class="font-semibold text-base text-gray-900 pr-4">
                        Comment utiliser ma carte cadeau
                        {{ product?.productName }}?
                      </h3>
                      <div
                        class="flex-shrink-0 w-8 h-8 flex items-center justify-center rounded-full transition-all duration-200"
                        :class="
                          openFaq === 0
                            ? 'bg-fuchsia-100 rotate-180'
                            : 'bg-gray-100'
                        "
                      >
                        <svg
                          class="w-4 h-4"
                          :class="
                            openFaq === 0 ? 'text-fuchsia-700' : 'text-gray-600'
                          "
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path
                            stroke-linecap="round"
                            stroke-linejoin="round"
                            stroke-width="2"
                            d="M19 9l-7 7-7-7"
                          />
                        </svg>
                      </div>
                    </button>
                    <div v-show="openFaq === 0" class="px-5 pb-5 pt-0">
                      <p class="text-gray-600 leading-relaxed">
                        Utilisez votre code cadeau sur la plateforme de
                        {{ product?.productName }} pour recharger votre compte
                        ou effectuer des achats.
                      </p>
                    </div>
                  </div>

                  <!-- FAQ Item 2 -->
                  <div
                    class="rounded-2xl bg-white border-2 border-gray-200 overflow-hidden transition-all duration-200"
                    :class="{ 'border-fuchsia-200 shadow-md': openFaq === 1 }"
                  >
                    <button
                      @click="toggleFaq(1)"
                      class="w-full text-left p-5 flex items-center justify-between hover:bg-gray-50 transition-colors duration-200"
                    >
                      <h3 class="font-semibold text-base text-gray-900 pr-4">
                        Combien de temps le code est-il valide?
                      </h3>
                      <div
                        class="flex-shrink-0 w-8 h-8 flex items-center justify-center rounded-full transition-all duration-200"
                        :class="
                          openFaq === 1
                            ? 'bg-fuchsia-100 rotate-180'
                            : 'bg-gray-100'
                        "
                      >
                        <svg
                          class="w-4 h-4"
                          :class="
                            openFaq === 1 ? 'text-fuchsia-700' : 'text-gray-600'
                          "
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path
                            stroke-linecap="round"
                            stroke-linejoin="round"
                            stroke-width="2"
                            d="M19 9l-7 7-7-7"
                          />
                        </svg>
                      </div>
                    </button>
                    <div v-show="openFaq === 1" class="px-5 pb-5 pt-0">
                      <p class="text-gray-600 leading-relaxed">
                        La validité de votre code cadeau dépend des conditions
                        de {{ product?.productName }}. Veuillez consulter leurs
                        conditions générales.
                      </p>
                    </div>
                  </div>

                  <!-- FAQ Item 3 -->
                  <div
                    class="rounded-2xl bg-white border-2 border-gray-200 overflow-hidden transition-all duration-200"
                    :class="{ 'border-fuchsia-200 shadow-md': openFaq === 2 }"
                  >
                    <button
                      @click="toggleFaq(2)"
                      class="w-full text-left p-5 flex items-center justify-between hover:bg-gray-50 transition-colors duration-200"
                    >
                      <h3 class="font-semibold text-base text-gray-900 pr-4">
                        Comment recevoir mon code?
                      </h3>
                      <div
                        class="flex-shrink-0 w-8 h-8 flex items-center justify-center rounded-full transition-all duration-200"
                        :class="
                          openFaq === 2
                            ? 'bg-fuchsia-100 rotate-180'
                            : 'bg-gray-100'
                        "
                      >
                        <svg
                          class="w-4 h-4"
                          :class="
                            openFaq === 2 ? 'text-fuchsia-700' : 'text-gray-600'
                          "
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path
                            stroke-linecap="round"
                            stroke-linejoin="round"
                            stroke-width="2"
                            d="M19 9l-7 7-7-7"
                          />
                        </svg>
                      </div>
                    </button>
                    <div v-show="openFaq === 2" class="px-5 pb-5 pt-0">
                      <p class="text-gray-600 leading-relaxed">
                        Après votre paiement, vous recevrez votre code par
                        e-mail dans les 30 secondes.
                      </p>
                    </div>
                  </div>
                </div>
              </section>
            </div>
          </div>
        </aside>
      </main>
    </template>
  </div>
</template>

<script setup>
import { ref, computed, onMounted, getCurrentInstance } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { Countries } from '@features/shared/libraries/countries';

const route = useRoute();
const router = useRouter();
const { proxy } = getCurrentInstance();
const { $stores } = proxy;

// Data
const product = ref(null);
const country = ref(null);
const loading = ref(true);
const error = ref(null);
const products = ref([]);

// Form state
const selectedAmount = ref(null);
const quantity = ref(1);
const customAmount = ref('');
const customAmountError = ref('');

// FAQ state
const openFaq = ref(null);

const toggleFaq = (index) => {
  openFaq.value = openFaq.value === index ? null : index;
};

// Parse slug from URL (format: productId-countryCode)
const parseSlug = () => {
  const slug = route.params.slug;
  if (!slug) return null;

  const parts = slug.split('-');
  if (parts.length < 2) return null;

  const productId = parseInt(parts[0]);
  const countryCode = parts[1].toUpperCase();

  return { productId, countryCode };
};

// Available amounts based on product - NO DEFAULTS, must be validated before
const availableAmounts = computed(() => {
  if (product.value?.denominationType === 'FIXED') {
    return product.value?.fixedRecipientDenominations || [];
  }
  // For range, show suggested amounts from the product if available
  return product.value?.fixedRecipientDenominations || [];
});

// Check if product supports range amounts
const isRangeAmount = computed(() => {
  return (
    product.value?.denominationType === 'RANGE' ||
    (product.value?.minSenderDenomination &&
      product.value?.maxSenderDenomination)
  );
});

const minAmount = computed(() => {
  return product.value?.minRecipientDenomination || null;
});

const maxAmount = computed(() => {
  return product.value?.maxRecipientDenomination || null;
});

const validateCustomAmount = () => {
  if (!customAmount.value) {
    customAmountError.value = '';
    selectedAmount.value = null;
    return;
  }

  const amount = parseFloat(customAmount.value);
  if (isNaN(amount)) {
    customAmountError.value = 'Veuillez entrer un montant valide';
    selectedAmount.value = null;
    return;
  }

  if (amount < minAmount.value) {
    customAmountError.value = `Montant minimum: ${minAmount.value} ${productCurrencySymbol.value}`;
    selectedAmount.value = null;
    return;
  }

  if (amount > maxAmount.value) {
    customAmountError.value = `Montant maximum: ${maxAmount.value} ${productCurrencySymbol.value}`;
    selectedAmount.value = null;
    return;
  }

  customAmountError.value = '';
  selectedAmount.value = amount;
};

const incrementQuantity = () => {
  quantity.value++;
};

const decrementQuantity = () => {
  if (quantity.value > 1) {
    quantity.value--;
  }
};

// Product currency
const productCurrency = computed(() => {
  return product.value?.recipientCurrencyCode || 'USD';
});

const productCurrencySymbol = computed(() => {
  const symbols = {
    USD: '$',
    EUR: '€',
    GBP: '£',
    XOF: 'FCFA',
    XAF: 'FCFA'
  };
  return symbols[productCurrency.value] || productCurrency.value;
});

const localCurrency = computed(() => {
  return country.value?.currency || 'XOF';
});

const productLogo = computed(() => {
  const logos = product.value?.logoUrls;
  if (logos && logos.length > 0) {
    return logos[1] || logos[0];
  }
  return '/images/placeholder.png';
});

const formatPrice = (price) => {
  return new Intl.NumberFormat('fr-FR').format(Math.round(price));
};

const formatPriceWithCurrency = (price) => {
  const formatted = new Intl.NumberFormat('fr-FR').format(Math.round(price));
  const currency = localCurrency.value;
  const symbols = {
    'XOF': 'FCFA',
    'XAF': 'FCFA',
    'USD': '$',
    'EUR': '€',
    'GBP': '£'
  };
  const symbol = symbols[currency] || currency;
  return `${formatted} ${symbol}`;
};

const calculateTotal = () => {
  if (!selectedAmount.value) return 0;
  const exchangeRate =
    product.value?.recipientCurrencyToSenderCurrencyExchangeRate || 600;
  const localPrice = selectedAmount.value * exchangeRate;
  return localPrice * quantity.value;
};

// Recipient total (selected amount * quantity) for display on buy button
const calculateRecipientTotal = () => {
  if (!selectedAmount.value) return 0;
  return selectedAmount.value * quantity.value;
};

const fetchProducts = async (countryCode) => {
  loading.value = true;
  error.value = null;

  try {
    const { bus } = await import('@core/events/eventSystem');

    return new Promise((resolve, reject) => {
      let timeoutId;
      let handled = false;

      const cleanup = () => {
        if (timeoutId) clearTimeout(timeoutId);
        bus.off('cards.fetch_by_country_success', successHandler);
        bus.off('cards.fetch_by_country_error', errorHandler);
      };

      const successHandler = (data) => {
        if (handled) return;
        handled = true;
        cleanup();
        products.value = data.data || data || [];
        loading.value = false;
        resolve();
      };

      const errorHandler = (err) => {
        if (handled) return;
        handled = true;
        cleanup();
        error.value = 'Erreur lors du chargement des produits';
        loading.value = false;
        reject(err);
      };

      // Set timeout for 10 seconds
      timeoutId = setTimeout(() => {
        if (handled) return;
        handled = true;
        cleanup();
        error.value = "Délai d'attente dépassé. Veuillez réessayer.";
        loading.value = false;
        reject(new Error('Timeout'));
      }, 10000);

      bus.on('cards.fetch_by_country_success', successHandler);
      bus.on('cards.fetch_by_country_error', errorHandler);

      bus.emit('cards.fetch_by_country', countryCode);
    });
  } catch (e) {
    error.value = 'Erreur lors du chargement des produits';
    loading.value = false;
  }
};

const goToCheckout = () => {
  if (!selectedAmount.value) {
    return;
  }

  router.push({
    path: '/checkout/quick',
    query: {
      productId: product.value?.productId,
      amount: selectedAmount.value,
      quantity: quantity.value,
      orderType: 'gift_card',
      countryCode: country.value?.countryCode || 'BJ'
    }
  });
};

onMounted(async () => {
  const parsed = parseSlug();

  if (!parsed) {
    error.value = 'URL invalide';
    loading.value = false;
    return;
  }

  const { productId, countryCode } = parsed;

  // Find country from library
  const foundCountry = Countries.find(countryCode);
  if (!foundCountry) {
    error.value = 'Pays non trouvé';
    loading.value = false;
    return;
  }

  country.value = foundCountry;

  // Update global store with selected country
  $stores.global.selectCountry(countryCode);

  // Fetch products from API
  await fetchProducts(countryCode);

  // Find the specific product
  const foundProduct = products.value.find((p) => p.productId === productId);
  if (!foundProduct) {
    error.value = 'Produit non trouvé';
    loading.value = false;
    return;
  }

  // Validate product has required amount data
  const hasValidAmounts =
    foundProduct.recipientCurrencyCode &&
    ((foundProduct.fixedRecipientDenominations && foundProduct.fixedRecipientDenominations.length > 0) ||
     (foundProduct.minRecipientDenomination && foundProduct.maxRecipientDenomination));

  if (!hasValidAmounts) {
    error.value = 'Les données de ce produit sont incomplètes. Veuillez contacter le support.';
    loading.value = false;
    return;
  }

  product.value = foundProduct;

  // Set default amount - only if we have fixed denominations
  if (availableAmounts.value.length > 0) {
    // Use second amount as default (usually the middle option), or first if only one
    selectedAmount.value = availableAmounts.value.length > 1
      ? availableAmounts.value[1]
      : availableAmounts.value[0];
  } else if (minAmount.value && maxAmount.value) {
    // For range products, don't set a default - user must choose
    selectedAmount.value = null;
  }
});
</script>

<style scoped>
/* Custom transitions for FAQ */
.bg-gray-100 > div {
  transition: all 0.3s ease;
}
</style>
